import { useEffect, useMemo, useRef, useState } from 'react'
import { CalendarRange, ChevronDown, Download, Loader2, MapPin, TriangleAlert, Zap } from 'lucide-react'
import { fetchReport } from '../useReportData'
import { pollWhileVisible } from '../../lib/pollWhileVisible'
import { reportCard, reportCardPad, reportEyebrow } from '../ui'
import { CC } from '../../cc/tokens'
import type { ReportRow } from '../../lib/types'

/**
 * The round trip, third pass: the infographic rebuild.
 *
 * WHY THE LAST VERSION READ AS "NOT TO SCALE". It sized each card with
 * `flexGrow: typical_hours` -- mathematically proportional, visually a lie, because the
 * site visits (2.7-4.3 h) and single-direction legs (2.2-5.7 h) at this corridor sit in
 * one narrow numeric band, and per-card min-widths flattened what little difference was
 * left. Five near-identical boxes under a header claiming "drawn to scale".
 *
 * THIS VERSION SEPARATES THE TWO JOBS THE OLD ROW CONFLATED:
 *
 *  1. SCALE lives in the LAP RIBBON: one typical lap unrolled chronologically --
 *     Dhar, the loaded run south, Jhulwania, on to Dhule, and back the same way -- every
 *     block's width a share of the ~24 stitched hours, with an hour axis underneath so
 *     the proportions can be checked against real ticks. Jhulwania appears twice because
 *     a truck genuinely stops there twice a lap. Each block is its own median, so the
 *     strip runs a little longer than the lap median (medians do not sum); the caption
 *     says so instead of hiding it.
 *
 *  2. NUMBERS live in the CARD GRID below: equal-width on purpose, each carrying the
 *     best-1-in-10 / typical / worst-1-in-10 triple on a shared-scale range bar. The
 *     operator's complaint -- "18h typical, 25h worst paints a wrong picture" -- is a
 *     complaint about a median shown with only its bad tail; the p10 now renders
 *     everywhere the p50/p90 pair does (headline, sites, legs).
 *
 * THE CHARGING CORRECTION. The yard phase bar used to be captioned "yard (charging)"
 * while actually measuring the whole yard-fence dwell -- charging, queueing and idle in
 * one number under a charging-sounding name. The pack's charge-current record
 * (v_hub_time_split, per yard fence) already knows the true split, so the yard bar is
 * now drawn as two sub-segments -- charging (amber) and parked-not-charging (slate) --
 * with the split estimated from that fence's accumulated charging share. One bar, one
 * honest story, at Dhar and Dhule and the Jhulwania depot halt alike.
 *
 * The drill-downs keep every mechanism that was already right (v_site_phase_visit,
 * v_hub_time_split, v_hub_visit_detail, v_leg_segment_mix) restyled to the same visual
 * language: range bars for spreads, amber-for-charging, mono for every number.
 */

// ------------------------------------------------------------------------------- data shape
interface SiteVisit {
  place: string
  visits: number
  best_1_in_10_hours: number
  typical_hours: number
  worst_1_in_10_hours: number
  total_hours: number
}

interface SitePhase {
  place: string
  phase: string
  time_class: string
  visits: number
  typical_hours: number
  total_hours: number
}

interface RoadLeg {
  stage_name: string
  load_state: string | null
  observations: number
  best_1_in_10_hours: number
  typical_hours: number
  worst_1_in_10_hours: number
  typical_km: number | null
}

interface HubStep {
  hub_code: string
  step: number
  activity: string
  visits: number
  typical_hours: number
  worst_1_in_10_hours: number
  share_of_hours_pct: number
  charging: boolean
  can_we_cut_it: string
}

interface LegSegment {
  stage_name: string
  segment: string
  time_class: string
  crossings: number
  typical_hours: number
  total_hours: number
  share_of_leg_pct: number
}

interface HubVisit {
  visit_id: number
  vehicle_id: string
  hub_code: string
  arrival_ts: number
  dwell_hours: number
  charging_hours: number
  soc_arrival: number | null
  soc_departure: number | null
  tat_breached: boolean | null
}

interface CycleHeadline {
  full_laps: number
  cycles_seen: number
  best_1_in_10_hours: number | null
  typical_lap_hours: number | null
  worst_1_in_10_hours: number | null
}

interface DataRange {
  earliest: string | null
  latest: string | null
}

type Selection = { kind: 'site'; place: string } | { kind: 'road'; name: string }

function num(r: ReportRow, k: string): number {
  const v = r[k]
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
function str(r: ReportRow, k: string): string {
  const v = r[k]
  return v == null ? '' : String(v)
}
function bool(r: ReportRow, k: string): boolean {
  return r[k] === true || r[k] === 't' || r[k] === 'true'
}

/** DHAR-YARD, DHULE-YARD-IN etc. are fence-level hub_codes; the site views know only the
 *  three physical places -- the same prefix rule v_stage_geo uses server-side. */
function placeOf(hubCode: string): string {
  if (hubCode.startsWith('DHAR')) return 'DHAR'
  if (hubCode.startsWith('DHULE')) return 'DHULE'
  if (hubCode.startsWith('JHUL')) return 'JHUL'
  return hubCode
}

function fmtHours(h: number): string {
  if (!Number.isFinite(h)) return '—'
  if (h >= 24) return `${Math.round(h)}h`
  if (h < 1) return `${Math.round(h * 60)}m`
  const whole = Math.floor(h)
  const mins = Math.round((h - whole) * 60)
  return mins === 0 ? `${whole}h` : `${whole}h ${mins}m`
}

/** necessary = the job (green), controllable = cuttable (rose), unknown = not yet
 *  classified (neutral) -- the same triple the "Where the time goes" report uses. */
function classColor(tc: string): string {
  if (tc === 'controllable') return CC.dangerBright
  if (tc === 'necessary') return CC.okBright
  return '#94A3B8'
}

const CLASS_LABEL: Record<string, string> = {
  necessary: 'necessary',
  controllable: 'controllable',
  unknown: 'not classified',
}

const SITE_LABEL: Record<string, string> = { DHAR: 'Dhar', JHUL: 'Jhulwania', DHULE: 'Dhule' }

/** The real chronological sequence at each site -- operating knowledge confirmed against
 *  the geotag record (see db/reports_operations.sql, section 1c), not layout guesswork. */
const SITE_SEQUENCE: Record<string, string> = {
  DHAR:
    'One visit: yard (parking + charging) → gate-2 queue → gate 2 → plant (loading) → ' +
    'tarpaulin → gate 1 → back through the yard to close the visit.',
  JHUL:
    'One fence covers the whole depot halt. Charging vs rest is not a separate fence — ' +
    'it is read from the charging record, shown below.',
  DHULE:
    'One visit: gate-in queue → gate in → yard → gate out. Gate (the plant boundary, in + out) ' +
    'and yard (parking/charging) are tracked as separate phases here — gate-crossing time is ' +
    'the closest signal this system has to time at the plant, even without a dedicated plant ' +
    'fence like Dhar has.',
}

/** The dwell fence measures presence, not current. "yard (charging)" was the old caption
 *  and the operator rightly read it as overstated -- the whole dwell is NOT charge time. */
function phaseLabel(place: string, phase: string): string {
  if (place === 'DHAR' && phase === 'yard') return 'yard (parking + charging)'
  if (place === 'DHULE' && phase === 'gate') return 'gate (plant in/out)'
  if (place === 'DHULE' && phase === 'yard') return 'yard (parking + charging)'
  return phase
}

/** The phase at each place whose fence dwell mixes charging with plain parking. */
const YARD_PHASE: Record<string, string> = { DHAR: 'yard', DHULE: 'yard', JHUL: 'depot halt' }

/** The hub_time_split fence that instruments that yard: the dedicated -YARD fence where
 *  one exists, else the bare place fence (Jhulwania's single fence IS its yard). */
function yardHubCode(place: string, hubSteps: HubStep[] | null): string | null {
  if (!hubSteps) return null
  const codes = [...new Set(hubSteps.filter((s) => placeOf(s.hub_code) === place).map((s) => s.hub_code))]
  return codes.find((c) => c.includes('YARD')) ?? (codes.includes(place) ? place : codes[0] ?? null)
}

/** Fraction of yard-fence hours actually drawing charge, from the pack's charge-current
 *  counter (share_of_hours_pct of the charging step, which sums to 100 across the three
 *  steps of one fence). Applied to the median dwell it is an estimate -- the best split
 *  the record gives, and far more honest than captioning the whole dwell "charging". */
function chargingFraction(place: string, hubSteps: HubStep[] | null): number | null {
  const code = yardHubCode(place, hubSteps)
  if (!code || !hubSteps) return null
  const chg = hubSteps.find((s) => s.hub_code === code && s.charging)
  if (!chg) return null
  const f = chg.share_of_hours_pct / 100
  return f >= 0 && f <= 1 ? f : null
}

// ------------------------------------------------------------------------------------ main
export function RoundTripJourney(): JSX.Element {
  const [sites, setSites] = useState<SiteVisit[] | null>(null)
  const [sitePhases, setSitePhases] = useState<SitePhase[] | null>(null)
  const [roads, setRoads] = useState<RoadLeg[] | null>(null)
  const [hubSteps, setHubSteps] = useState<HubStep[] | null>(null)
  const [legMix, setLegMix] = useState<LegSegment[] | null>(null)
  const [hubVisits, setHubVisits] = useState<HubVisit[] | null>(null)
  const [headline, setHeadline] = useState<CycleHeadline | null>(null)
  const [dataRange, setDataRange] = useState<DataRange | null>(null)
  const [dateWindow, setDateWindow] = useState<{ from: string; to: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Selection | null>(null)
  const [mounted, setMounted] = useState(false)
  // A refetch that is REPLACING numbers already on screen, as opposed to the first load.
  // QA reported "tried changing date - data doesn't change": the backend was honouring the
  // range correctly the whole time, but the only loading state on this page was the
  // full-page spinner below, gated on `!sites`, which can fire exactly once. Every later
  // load -- the one a date change triggers above all -- left the previous window's numbers
  // sitting there, unchanged and unannotated, for the 15-17s the fetch took. A page that
  // looks identical for seventeen seconds after you touch a control is indistinguishable
  // from a broken one, and was reasonably read as such.
  const [refreshing, setRefreshing] = useState(false)
  // A ref, not `sites`: the load effect below depends only on [dateWindow], so its closure
  // captures `sites === null` for its entire life and could never tell a first load from
  // its own 5-minute poll.
  const loadedOnce = useRef(false)

  useEffect(() => {
    // Wait for the data-range effect below to set the real default window. Without this
    // guard, this effect's first run (on mount, before that fetch resolves) sees
    // `dateWindow === null`, passes `range = undefined`, and fires one unbounded fetch
    // over the corridor's entire history -- exactly the load the 3-day default exists to
    // avoid -- immediately followed by a second, bounded fetch once the default lands.
    if (dateWindow === null) return undefined
    let live = true
    const load = async () => {
      if (loadedOnce.current) setRefreshing(true)
      try {
        // hub_time_split stays fleet-wide regardless of the picker -- it's built straight
        // from the hub_visit table, not the expensive f_stage_fact() chain, so it was never
        // the slow one and has no bounded variant to pass a range to (a range it doesn't
        // support is a 400, not a silent no-op). site_phase_visit and leg_segment_mix used
        // to be fleet-wide for the same "no bounded variant" reason -- they now have one
        // (db/reports_operations.sql: f_site_phase_visit/f_leg_segment_mix), confirmed on
        // production at 66.6s and 32.7s unbounded, the real remaining cost once
        // f_site_visit's own fix landed.
        // dateWindow values are already full datetime-local timestamps, not bare dates --
        // no end-of-day suffix needed.
        const range = dateWindow ? { from: dateWindow.from, to: dateWindow.to } : undefined
        const [sv, sp, rt, hs, lm, ch, hv] = await Promise.all([
          fetchReport('site_visit', 10, range),
          fetchReport('site_phase_visit', 50, range),
          fetchReport('round_trip_tat', 200, range),
          fetchReport('hub_time_split', 200),
          fetchReport('leg_segment_mix', 200, range),
          fetchReport('cycle_headline', 1, range),
          // Ordered newest-first by the view itself; enough rows to give every site a
          // decent recent slice even though the busiest fence crowds out the quietest.
          fetchReport('hub_visit_detail', 1500, range),
        ])
        if (!live) return
        setSites(sv.map((r) => ({
          place: str(r, 'place'), visits: num(r, 'visits'),
          best_1_in_10_hours: num(r, 'best_1_in_10_hours'),
          typical_hours: num(r, 'typical_hours'),
          worst_1_in_10_hours: num(r, 'worst_1_in_10_hours'),
          total_hours: num(r, 'total_hours'),
        })))
        setSitePhases(sp.map((r) => ({
          place: str(r, 'place'), phase: str(r, 'phase'), time_class: str(r, 'time_class'),
          visits: num(r, 'visits'), typical_hours: num(r, 'typical_hours'),
          total_hours: num(r, 'total_hours'),
        })))
        // Only the road rows. The hub rows of round_trip_tat grain on single fences --
        // fragments of a visit -- and are exactly what this rebuild retired at the top level.
        setRoads(rt.filter((r) => str(r, 'kind') === 'On the road').map((r) => ({
          stage_name: str(r, 'stage_name'),
          load_state: r.load_state == null ? null : str(r, 'load_state'),
          observations: num(r, 'observations'),
          best_1_in_10_hours: num(r, 'best_1_in_10_hours'),
          typical_hours: num(r, 'typical_hours'),
          worst_1_in_10_hours: num(r, 'worst_1_in_10_hours'),
          typical_km: r.typical_km == null ? null : num(r, 'typical_km'),
        })))
        setHubSteps(hs.map((r) => ({
          hub_code: str(r, 'hub_code'), step: num(r, 'step'), activity: str(r, 'activity'),
          visits: num(r, 'visits'), typical_hours: num(r, 'typical_hours'),
          worst_1_in_10_hours: num(r, 'worst_1_in_10_hours'),
          share_of_hours_pct: num(r, 'share_of_hours_pct'), charging: bool(r, 'charging'),
          can_we_cut_it: str(r, 'can_we_cut_it'),
        })))
        setLegMix(lm.map((r) => ({
          stage_name: str(r, 'stage_name'), segment: str(r, 'segment'),
          time_class: str(r, 'time_class'), crossings: num(r, 'crossings'),
          typical_hours: num(r, 'typical_hours'), total_hours: num(r, 'total_hours'),
          share_of_leg_pct: num(r, 'share_of_leg_pct'),
        })))
        if (ch[0]) {
          const r = ch[0]
          setHeadline({
            full_laps: num(r, 'full_laps'), cycles_seen: num(r, 'cycles_seen'),
            best_1_in_10_hours: r.best_1_in_10_hours == null ? null : num(r, 'best_1_in_10_hours'),
            typical_lap_hours: r.typical_lap_hours == null ? null : num(r, 'typical_lap_hours'),
            worst_1_in_10_hours: r.worst_1_in_10_hours == null ? null : num(r, 'worst_1_in_10_hours'),
          })
        }
        setHubVisits(hv.map((r) => ({
          visit_id: num(r, 'visit_id'), vehicle_id: str(r, 'vehicle_id'),
          hub_code: str(r, 'hub_code'),
          arrival_ts: Date.parse(str(r, 'arrival_ts')),
          dwell_hours: num(r, 'dwell_hours'), charging_hours: num(r, 'charging_hours'),
          soc_arrival: r.soc_arrival == null ? null : num(r, 'soc_arrival'),
          soc_departure: r.soc_departure == null ? null : num(r, 'soc_departure'),
          tat_breached: r.tat_breached == null ? null : bool(r, 'tat_breached'),
        })))
        setError(null)
        // Only after a load has actually put numbers on screen -- a first load that threw
        // renders the error card, not stale data, and the NEXT attempt is still a first
        // load as far as the indicator is concerned.
        loadedOnce.current = true
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : String(e))
      } finally {
        // `live` only: a load abandoned by a newer date window must not clear the
        // indicator the newer one just set.
        if (live) setRefreshing(false)
      }
    }
    const stop = pollWhileVisible(() => void load(), 5 * 60_000)
    return () => { live = false; stop() }
  }, [dateWindow])

  // The bounds of the record, fetched once: a picker with no floor lets someone select
  // 2020 and read an honestly-empty report as "nothing happened".
  useEffect(() => {
    let live = true
    fetch('/api/reports/data-range')
      .then((r) => (r.ok ? (r.json() as Promise<DataRange>) : null))
      .then((d) => {
        if (!live || !d || !d.earliest || !d.latest) return
        setDataRange(d)
        // Default to the last 3 days of the record, not its entire span -- a corridor
        // running since launch otherwise opens this page averaged over months on first
        // load (was 7 days; even at 7, f_site_visit's per-visit computation still took
        // long enough to notice -- 3 trims the working set further). Clamped to the
        // record's own earliest so a young dataset doesn't default to a window that
        // starts before any data exists.
        const latestMs = Date.parse(d.latest)
        const earliestMs = Date.parse(d.earliest)
        const weekAgoMs = Math.max(earliestMs, latestMs - 3 * 24 * 60 * 60 * 1000)
        setDateWindow({
          from: new Date(weekAgoMs).toISOString().slice(0, 16),
          to: d.latest.slice(0, 16),
        })
      })
      .catch(() => undefined)
    return () => { live = false }
  }, [])

  useEffect(() => {
    if (sites) {
      const t = setTimeout(() => setMounted(true), 40)
      return () => clearTimeout(t)
    }
  }, [sites])

  const corridor = useMemo(() => {
    if (!sites || !roads) return null
    const site = (p: string) => sites.find((s) => s.place === p) ?? null
    const leg = (n: string) => roads.find((r) => r.stage_name === n) ?? null
    return {
      dhar: site('DHAR'), jhul: site('JHUL'), dhule: site('DHULE'),
      dj: { south: leg('DHAR → JHUL'), north: leg('JHUL → DHAR') },
      jd: { south: leg('JHUL → DHULE'), north: leg('DHULE → JHUL') },
    }
  }, [sites, roads])

  const split = useMemo(() => {
    if (!sites || !roads) return null
    const siteHours = sites.reduce((s, x) => s + x.typical_hours, 0)
    const roadHours = roads.reduce((s, x) => s + x.typical_hours, 0)
    if (siteHours + roadHours <= 0) return null
    return { siteHours, roadHours, sitePct: (100 * siteHours) / (siteHours + roadHours) }
  }, [sites, roads])

  // One classic lap in chronological order. Jhulwania appears twice because a truck
  // stops there both ways; each block is its own median across many trucks, so the
  // stitched total intentionally exceeds the lap median -- the caption owns that.
  const lap = useMemo(() => {
    if (!corridor) return null
    type Seg =
      | { kind: 'site'; site: SiteVisit; pass: 1 | 2 }
      | { kind: 'road'; leg: RoadLeg; dir: 'south' | 'north' }
    const segs: Seg[] = []
    if (corridor.dhar) segs.push({ kind: 'site', site: corridor.dhar, pass: 1 })
    if (corridor.dj.south) segs.push({ kind: 'road', leg: corridor.dj.south, dir: 'south' })
    if (corridor.jhul) segs.push({ kind: 'site', site: corridor.jhul, pass: 1 })
    if (corridor.jd.south) segs.push({ kind: 'road', leg: corridor.jd.south, dir: 'south' })
    if (corridor.dhule) segs.push({ kind: 'site', site: corridor.dhule, pass: 1 })
    if (corridor.jd.north) segs.push({ kind: 'road', leg: corridor.jd.north, dir: 'north' })
    if (corridor.jhul) segs.push({ kind: 'site', site: corridor.jhul, pass: 2 })
    if (corridor.dj.north) segs.push({ kind: 'road', leg: corridor.dj.north, dir: 'north' })
    const hoursOf = (s: Seg) => (s.kind === 'site' ? s.site.typical_hours : s.leg.typical_hours)
    const total = segs.reduce((a, s) => a + hoursOf(s), 0)
    if (segs.length === 0 || total <= 0) return null
    // Proportional against the FULL stitched lap, with a floor so tiny blocks stay
    // clickable, renormalised so the floors do not silently distort the rest.
    const FLOOR_PCT = 4
    const raw = segs.map((s) => Math.max((100 * hoursOf(s)) / total, FLOOR_PCT))
    const scale = 100 / raw.reduce((a, b) => a + b, 0)
    const widths = raw.map((r) => r * scale)
    const parked = segs.filter((s) => s.kind === 'site').reduce((a, s) => a + hoursOf(s), 0)
    return { segs, widths, total, parked, driving: total - parked }
  }, [corridor])

  const siteSpreadMax = useMemo(
    () => (sites ? sites.reduce((m, s) => Math.max(m, s.worst_1_in_10_hours), 0) : 0),
    [sites],
  )
  const legSpreadMax = useMemo(() => {
    if (!corridor) return 0
    const legs = [corridor.dj.south, corridor.dj.north, corridor.jd.south, corridor.jd.north]
    return legs.reduce((m, l) => Math.max(m, l?.worst_1_in_10_hours ?? 0), 0)
  }, [corridor])

  const selectedSite = useMemo(() => {
    if (!selected || selected.kind !== 'site' || !sites) return null
    return sites.find((s) => s.place === selected.place) ?? null
  }, [selected, sites])

  const selectedRoad = useMemo(() => {
    if (!selected || selected.kind !== 'road' || !roads) return null
    return roads.find((r) => r.stage_name === selected.name) ?? null
  }, [selected, roads])

  if (error) {
    return (
      <div className={`${reportCard} ${reportCardPad} max-w-xl`}>
        <div className="flex items-center gap-2 text-rose-700">
          <TriangleAlert size={16} />
          <span className="text-sm font-medium">Could not load the round trip</span>
        </div>
        <p className="mt-1 text-[12.5px] text-slate-500">{error}</p>
      </div>
    )
  }

  if (!sites || !corridor) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 size={15} className="animate-spin" /> loading the round trip…
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className={reportEyebrow}>The round trip</div>
            <h1 className="mt-1 text-xl font-semibold text-slate-900">
              Dhar <ArrowGlyph /> Jhulwania <ArrowGlyph /> Dhule <ArrowGlyph /> Jhulwania <ArrowGlyph /> Dhar
            </h1>
            <p className="mt-1.5 max-w-2xl text-[13px] leading-relaxed text-slate-500">
              One typical lap unrolled hour by hour, then the numbers behind every stop and
              leg. A site&apos;s figure is one whole visit: every fence a truck touches there,
              summed, then the median across visits. Click anything to drill in.
            </p>
          </div>
          {/* Directly under the picker, not somewhere neutral: a date change is the one
              interaction that triggers a visibly-nothing 15s refetch, so the feedback
              belongs where the eye already is. aria-live so it is announced, not just
              drawn. */}
          <div className="flex flex-col items-end gap-1.5">
            <DataWindow range={dataRange} value={dateWindow} onChange={setDateWindow} />
            {refreshing && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 font-mono text-[9.5px] font-medium uppercase tracking-[0.14em] text-amber-700"
              >
                <Loader2 size={10} className="animate-spin" />
                refreshing
              </div>
            )}
          </div>
        </div>
        {headline && headline.typical_lap_hours != null && (
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <HeadlineTile
              label="best 1 in 10 laps" value={headline.best_1_in_10_hours}
              color={CC.ok} sub="the fastest tenth" mounted={mounted} delay={0}
            />
            <HeadlineTile
              label="typical lap" value={headline.typical_lap_hours}
              color={CC.ink} sub="the median, half faster half slower" accent
              mounted={mounted} delay={50}
            />
            <HeadlineTile
              label="worst 1 in 10 laps" value={headline.worst_1_in_10_hours}
              color={CC.danger} sub="the slowest tenth" mounted={mounted} delay={100}
            />
            <div
              className="rounded-xl border border-[rgba(15,23,42,0.07)] bg-white/70 px-4 py-3 transition-all duration-500 ease-out motion-reduce:transition-none"
              style={{ opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(6px)', transitionDelay: '150ms' }}
            >
              <div className="font-mono text-[9.5px] font-medium uppercase tracking-[0.14em] text-[#8794A7]">laps measured</div>
              <div className="mt-1 font-mono text-[22px] font-bold leading-none text-slate-900">{headline.full_laps}</div>
              <div className="mt-1 text-[10.5px] text-slate-500">of {headline.cycles_seen} cycles seen</div>
            </div>
          </div>
        )}
      </header>

      {lap && (
        <section className={`${reportCard} ${reportCardPad}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className={reportEyebrow}>One lap, unrolled — to scale</span>
            <span className="text-[11.5px] text-slate-500">
              <span className="font-mono font-semibold" style={{ color: CC.warn }}>{fmtHours(lap.parked)}</span>
              {' '}parked at the sites ·{' '}
              <span className="font-mono font-semibold text-slate-800">{fmtHours(lap.driving)}</span>
              {' '}driving
            </span>
          </div>
          <LapRibbon
            lap={lap} sitePhases={sitePhases} hubSteps={hubSteps}
            selected={selected} onSelect={setSelected} mounted={mounted}
          />
          <p className="mt-2 text-[10.5px] leading-relaxed text-slate-400">
            Every block&apos;s width is its median hours against the whole strip, on the hour
            ruler above. Jhulwania appears twice — a truck stops there both ways. Each block
            is its own median, so the strip stitches to {fmtHours(lap.total)}, a little more
            than the {headline?.typical_lap_hours != null ? fmtHours(headline.typical_lap_hours) : '—'} median
            lap: medians do not sum.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[10px] uppercase tracking-wide text-slate-400">
            <LegendDot color={CC.warnBright} label="charging" />
            <LegendDot color={CC.okBright} label="necessary" />
            <LegendDot color={CC.dangerBright} label="controllable" />
            <LegendDot color="#94A3B8" label="parked, not charging / unclassified" />
            <span className="text-slate-200">|</span>
            <LegendDot color={CC.south} label="southbound · loaded" />
            <LegendDot color={CC.north} label="northbound · unloaded" />
          </div>
        </section>
      )}

      {split && (
        <section className={`${reportCard} px-6 py-4`}>
          <div className="flex items-baseline justify-between gap-3">
            <span className={reportEyebrow}>Parked vs driving, per lap</span>
            <span className="text-[12px] text-slate-500">
              a typical lap spends{' '}
              <span className="font-mono font-semibold text-slate-900">{fmtHours(split.siteHours)}</span>
              {' '}sitting at the three sites and{' '}
              <span className="font-mono font-semibold text-slate-900">{fmtHours(split.roadHours)}</span>
              {' '}on the road
            </span>
          </div>
          <div className="mt-2.5 flex h-3 overflow-hidden rounded-full">
            <div
              className="transition-all duration-700 ease-out motion-reduce:transition-none"
              style={{ width: mounted ? `${split.sitePct}%` : '0%', background: CC.warnBright }}
              title={`At the sites: ${fmtHours(split.siteHours)}`}
            />
            <div className="flex-1" style={{ background: 'rgba(15,23,42,0.10)' }} title={`On the road: ${fmtHours(split.roadHours)}`} />
          </div>
          <div className="mt-1.5 flex justify-between font-mono text-[10px] uppercase tracking-wide text-slate-400">
            <span style={{ color: CC.warn }}>{split.sitePct.toFixed(0)}% at the sites</span>
            <span>{(100 - split.sitePct).toFixed(0)}% driving — every recorded leg type, direct runs included</span>
          </div>
        </section>
      )}

      <section className={`${reportCard} ${reportCardPad}`}>
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <span className={reportEyebrow}>Every stop and leg — best · typical · worst 1 in 10</span>
          <span className="text-[10.5px] text-slate-400">range bars share one scale per row kind</span>
        </div>
        <div className="overflow-x-auto pb-1">
          <div className="grid min-w-[880px] grid-cols-5 items-stretch gap-2.5">
            {corridor.dhar && (
              <SiteCard
                site={corridor.dhar} phases={sitePhases} hubSteps={hubSteps} spreadMax={siteSpreadMax}
                selected={selected} onSelect={setSelected} mounted={mounted} delay={0}
              />
            )}
            <RoadPairCard
              south={corridor.dj.south} north={corridor.dj.north} spreadMax={legSpreadMax}
              selected={selected} onSelect={setSelected} mounted={mounted} delay={60}
            />
            {corridor.jhul && (
              <SiteCard
                site={corridor.jhul} phases={sitePhases} hubSteps={hubSteps} spreadMax={siteSpreadMax}
                selected={selected} onSelect={setSelected} mounted={mounted} delay={120}
              />
            )}
            <RoadPairCard
              south={corridor.jd.south} north={corridor.jd.north} spreadMax={legSpreadMax}
              selected={selected} onSelect={setSelected} mounted={mounted} delay={180}
            />
            {corridor.dhule && (
              <SiteCard
                site={corridor.dhule} phases={sitePhases} hubSteps={hubSteps} spreadMax={siteSpreadMax}
                selected={selected} onSelect={setSelected} mounted={mounted} delay={240}
              />
            )}
          </div>
        </div>
      </section>

      <DrillDown
        site={selectedSite} road={selectedRoad}
        sitePhases={sitePhases} hubSteps={hubSteps} hubVisits={hubVisits} legMix={legMix}
        onClose={() => setSelected(null)}
      />

      <section className={`${reportCard} flex flex-wrap items-center justify-between gap-3 px-6 py-4`}>
        <div>
          <div className="text-sm font-semibold text-slate-900">Take the whole record with you</div>
          <p className="mt-0.5 text-[12px] text-slate-500">
            Every base row and derived view behind this page, one spreadsheet — pick the
            grain: the cycle as a whole, everything anchored to a hub, or just the road legs.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {([
            ['round_trip', 'Round Trip'],
            ['hub_wise', 'Hub Wise'],
            ['one_leg', 'One leg'],
          ] as const).map(([view, label]) => (
            <a
              key={view}
              href={`/api/reports/round-trip-workbook.xlsx?view=${view}`}
              download
              title={view === 'one_leg' ? 'Loading to unloading -- the road legs alone' : undefined}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white
                         px-3 py-2 text-[12.5px] font-medium text-slate-700 shadow-sm
                         transition-colors hover:border-slate-300 hover:text-slate-900"
            >
              <Download size={14} />
              {label}
            </a>
          ))}
        </div>
      </section>
    </div>
  )
}

function ArrowGlyph(): JSX.Element {
  return <span className="mx-1 text-slate-300">→</span>
}

function LegendDot({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  )
}

// --------------------------------------------------------------------------- headline tile
function HeadlineTile({ label, value, color, sub, accent, mounted, delay }: {
  label: string
  value: number | null
  color: string
  sub: string
  accent?: boolean
  mounted: boolean
  delay: number
}): JSX.Element {
  return (
    <div
      className="rounded-xl border bg-white/70 px-4 py-3 transition-all duration-500 ease-out motion-reduce:transition-none"
      style={{
        borderColor: accent ? 'rgba(245,158,11,0.35)' : 'rgba(15,23,42,0.07)',
        boxShadow: accent ? '0 10px 30px -18px rgba(15,23,42,0.55)' : undefined,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'none' : 'translateY(6px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      <div className="font-mono text-[9.5px] font-medium uppercase tracking-[0.14em] text-[#8794A7]">{label}</div>
      <div className="mt-1 font-mono text-[22px] font-bold leading-none" style={{ color }}>
        {value == null ? '—' : fmtHours(value)}
      </div>
      <div className="mt-1 text-[10.5px] text-slate-500">{sub}</div>
    </div>
  )
}

// --------------------------------------------------------------------------- range triple
/** The best / typical / worst triple with a shared-scale range bar: the span runs p10→p90
 *  (green into rose), the dark tick is the median. `max` is shared across siblings so the
 *  bars compare with each other, not just with themselves. */
function RangeTriple({ best, typical, worst, max, dense }: {
  best: number
  typical: number
  worst: number
  max: number
  dense?: boolean
}): JSX.Element {
  const pct = (v: number) => Math.min(100, Math.max(0, (100 * v) / (max || 1)))
  const left = pct(best)
  const span = Math.max(pct(worst) - left, 2)
  return (
    <div className="w-full">
      <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="absolute inset-y-0 rounded-full"
          style={{
            left: `${left}%`, width: `${span}%`,
            background: 'linear-gradient(90deg, rgba(16,185,129,0.55), rgba(244,63,94,0.55))',
          }}
        />
        <div
          className="absolute inset-y-0 w-[2px] rounded-full bg-slate-900"
          style={{ left: `calc(${pct(typical)}% - 1px)` }}
        />
      </div>
      <div className={`mt-1 flex items-baseline justify-between gap-1 font-mono ${dense ? 'text-[9.5px]' : 'text-[10.5px]'}`}>
        <span title="best 1 in 10" style={{ color: CC.ok }}>{fmtHours(best)}</span>
        <span title="typical (median)" className="font-semibold text-slate-900">{fmtHours(typical)}</span>
        <span title="worst 1 in 10" style={{ color: CC.danger }}>{fmtHours(worst)}</span>
      </div>
      {!dense && (
        <div className="flex justify-between font-mono text-[7.5px] uppercase tracking-[0.1em] text-slate-400">
          <span>best 1-in-10</span>
          <span>typical</span>
          <span>worst 1-in-10</span>
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------------------- phase strip
/** The stacked in-site strip. The yard phase (the fence dwell that mixes charging with
 *  plain parking) is split into an amber charging sub-segment and a slate idle one, using
 *  the charge-current share from that yard's hub_time_split fence. */
function PhaseStrip({ place, phases, hubSteps, mounted, delay, className }: {
  place: string
  phases: SitePhase[] | null
  hubSteps: HubStep[] | null
  mounted: boolean
  delay: number
  className?: string
}): JSX.Element | null {
  const myPhases = (phases ?? [])
    .filter((p) => p.place === place)
    .sort((a, b) => b.typical_hours - a.typical_hours)
  if (myPhases.length === 0) return null
  const phaseSum = myPhases.reduce((s, p) => s + p.typical_hours, 0) || 1
  const chargeFrac = chargingFraction(place, hubSteps)

  const spans: { key: string; w: number; color: string; title: string }[] = []
  for (const p of myPhases) {
    const w = Math.max((100 * p.typical_hours) / phaseSum, 1.5)
    if (p.phase === YARD_PHASE[place] && chargeFrac != null && chargeFrac > 0 && chargeFrac < 1) {
      const chargeH = p.typical_hours * chargeFrac
      spans.push({
        key: `${p.phase}-charging`, w: w * chargeFrac, color: CC.warnBright,
        title: `${phaseLabel(place, p.phase)} — actually charging: ≈${fmtHours(chargeH)} of ${fmtHours(p.typical_hours)} (charge-current record)`,
      })
      spans.push({
        key: `${p.phase}-idle`, w: w * (1 - chargeFrac), color: '#94A3B8',
        title: `${phaseLabel(place, p.phase)} — parked, not charging: ≈${fmtHours(p.typical_hours - chargeH)}`,
      })
    } else {
      spans.push({
        key: p.phase, w, color: classColor(p.time_class),
        title: `${phaseLabel(place, p.phase)}: ${fmtHours(p.typical_hours)} typical (${CLASS_LABEL[p.time_class] ?? p.time_class})`,
      })
    }
  }

  return (
    <span className={`flex overflow-hidden rounded-full ${className ?? ''}`}>
      {spans.map((s, i) => (
        <span
          key={s.key}
          title={s.title}
          className="transition-all duration-500 ease-out motion-reduce:transition-none"
          style={{
            width: mounted ? `${s.w}%` : '0%',
            background: s.color,
            transitionDelay: `${delay + 150 + i * 40}ms`,
          }}
        />
      ))}
    </span>
  )
}

// ------------------------------------------------------------------------------ lap ribbon
interface LapData {
  segs: ({ kind: 'site'; site: SiteVisit; pass: 1 | 2 } | { kind: 'road'; leg: RoadLeg; dir: 'south' | 'north' })[]
  widths: number[]
  total: number
  parked: number
  driving: number
}

function LapRibbon({ lap, sitePhases, hubSteps, selected, onSelect, mounted }: {
  lap: LapData
  sitePhases: SitePhase[] | null
  hubSteps: HubStep[] | null
  selected: Selection | null
  onSelect: (s: Selection | null) => void
  mounted: boolean
}): JSX.Element {
  // Hour ruler: a tick every 4 stitched hours plus the far edge, so "to scale" is
  // checkable against real marks rather than asserted in a caption.
  const ticks: number[] = []
  for (let t = 0; t <= lap.total; t += 4) ticks.push(t)

  return (
    <div className="overflow-x-auto pb-1">
      <div className="min-w-[880px]">
        <div className="relative mt-3 h-4">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute top-0 flex h-full flex-col items-center"
              style={{ left: `${(100 * t) / lap.total}%` }}
            >
              <span className="font-mono text-[8.5px] text-slate-400">{t}h</span>
            </span>
          ))}
          <span className="absolute right-0 top-0 font-mono text-[8.5px] font-semibold text-slate-500">
            {fmtHours(lap.total)}
          </span>
        </div>
        <div className="relative mb-1 h-1.5 border-x border-slate-200">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute top-0 h-full w-px bg-slate-200"
              style={{ left: `${(100 * t) / lap.total}%` }}
            />
          ))}
        </div>
        <div className="flex h-[76px] items-stretch gap-[3px]">
          {lap.segs.map((seg, i) => {
            const w = lap.widths[i]
            if (seg.kind === 'site') {
              const isSel = selected?.kind === 'site' && selected.place === seg.site.place
              const isDim = selected != null && !isSel
              return (
                <button
                  key={`${seg.site.place}-${seg.pass}`}
                  type="button"
                  onClick={() => onSelect(isSel ? null : { kind: 'site', place: seg.site.place })}
                  title={`${SITE_LABEL[seg.site.place] ?? seg.site.place}${seg.pass === 2 ? ' (return stop)' : ''}: ${fmtHours(seg.site.typical_hours)} typical visit`}
                  className="relative flex min-w-[52px] flex-col justify-center overflow-hidden rounded-lg
                             border px-2 text-left transition-all duration-700 ease-out
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400
                             motion-reduce:transition-none"
                  style={{
                    width: mounted ? `${w}%` : '0%',
                    borderColor: isSel ? 'rgba(245,158,11,0.6)' : 'rgba(245,158,11,0.3)',
                    background: isSel ? 'rgba(245,158,11,0.16)' : 'rgba(245,158,11,0.08)',
                    opacity: mounted ? (isDim ? 0.4 : 1) : 0,
                    transitionDelay: `${i * 50}ms`,
                    boxShadow: isSel ? '0 10px 24px -12px rgba(15,23,42,0.35)' : undefined,
                  }}
                >
                  <span className="truncate font-mono text-[8.5px] font-semibold uppercase tracking-[0.1em] text-amber-800">
                    {SITE_LABEL[seg.site.place] ?? seg.site.place}
                    {seg.pass === 2 ? ' ↩' : ''}
                  </span>
                  <span className="mt-0.5 truncate font-mono text-[13px] font-bold leading-none text-slate-900">
                    {fmtHours(seg.site.typical_hours)}
                  </span>
                  <PhaseStrip
                    place={seg.site.place} phases={sitePhases} hubSteps={hubSteps}
                    mounted={mounted} delay={i * 50}
                    className="absolute inset-x-1.5 bottom-1.5 h-1.5"
                  />
                </button>
              )
            }
            const isSel = selected?.kind === 'road' && selected.name === seg.leg.stage_name
            const isDim = selected != null && !isSel
            const color = seg.dir === 'south' ? CC.south : CC.north
            const textColor = seg.dir === 'south' ? '#0369A1' : '#6D28D9'
            return (
              <button
                key={seg.leg.stage_name}
                type="button"
                onClick={() => onSelect(isSel ? null : { kind: 'road', name: seg.leg.stage_name })}
                title={`${seg.leg.stage_name} (${seg.leg.load_state ?? ''}): ${fmtHours(seg.leg.typical_hours)} typical`}
                className="relative min-w-[44px] self-center overflow-hidden rounded-md border px-1.5
                           py-2 transition-all duration-700 ease-out focus:outline-none
                           focus-visible:ring-2 motion-reduce:transition-none"
                style={{
                  width: mounted ? `${w}%` : '0%',
                  height: 44,
                  borderColor: isSel ? color : `${color}55`,
                  background: isSel ? `${color}2E` : `${color}14`,
                  opacity: mounted ? (isDim ? 0.4 : 1) : 0,
                  transitionDelay: `${i * 50}ms`,
                  boxShadow: isSel ? '0 10px 24px -12px rgba(15,23,42,0.35)' : undefined,
                }}
              >
                <span className="flex flex-col items-center justify-center leading-none">
                  <span className="font-mono text-[10px] font-bold" style={{ color: textColor }}>
                    {seg.dir === 'south' ? '→' : '←'}
                  </span>
                  <span className="mt-0.5 truncate font-mono text-[11px] font-semibold text-slate-800">
                    {fmtHours(seg.leg.typical_hours)}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------------------- site card
function SiteCard({ site, phases, hubSteps, spreadMax, selected, onSelect, mounted, delay }: {
  site: SiteVisit
  phases: SitePhase[] | null
  hubSteps: HubStep[] | null
  spreadMax: number
  selected: Selection | null
  onSelect: (s: Selection | null) => void
  mounted: boolean
  delay: number
}): JSX.Element {
  const isSel = selected?.kind === 'site' && selected.place === site.place
  const isDim = selected != null && !isSel
  const myPhases = (phases ?? [])
    .filter((p) => p.place === site.place)
    .sort((a, b) => b.typical_hours - a.typical_hours)
  const top = myPhases[0] ?? null
  const chargeFrac = chargingFraction(site.place, hubSteps)
  const yardPhase = myPhases.find((p) => p.phase === YARD_PHASE[site.place]) ?? null

  return (
    <button
      type="button"
      onClick={() => onSelect(isSel ? null : { kind: 'site', place: site.place })}
      className="flex flex-col rounded-xl border px-3.5 py-3 text-left transition-all
                 duration-300 ease-out focus:outline-none focus-visible:ring-2
                 focus-visible:ring-amber-400 focus-visible:ring-offset-1
                 motion-reduce:transition-none"
      style={{
        borderColor: isSel ? 'rgba(245,158,11,0.55)' : 'rgba(245,158,11,0.26)',
        background: isSel ? 'rgba(245,158,11,0.14)' : 'rgba(245,158,11,0.06)',
        opacity: mounted ? (isDim ? 0.45 : 1) : 0,
        transform: mounted ? (isSel ? 'translateY(-2px)' : 'none') : 'translateY(8px)',
        transitionDelay: `${delay}ms`,
        boxShadow: isSel ? '0 10px 24px -12px rgba(15,23,42,0.35)' : undefined,
      }}
    >
      <span className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-700">
        <MapPin size={11} />
        {SITE_LABEL[site.place] ?? site.place}
      </span>
      <span className="mt-2 font-mono text-[21px] font-bold leading-none text-slate-900">
        {fmtHours(site.typical_hours)}
      </span>
      <span className="mt-0.5 text-[10px] text-slate-500">typical whole visit</span>
      <span className="mt-2 block">
        <RangeTriple
          best={site.best_1_in_10_hours} typical={site.typical_hours}
          worst={site.worst_1_in_10_hours} max={spreadMax}
        />
      </span>
      <PhaseStrip
        place={site.place} phases={phases} hubSteps={hubSteps}
        mounted={mounted} delay={delay} className="mt-2.5 h-2.5 w-full"
      />
      <span className="mt-1.5 truncate text-[10.5px] text-slate-500">
        {yardPhase && chargeFrac != null
          ? <>
              <Zap size={9} className="mr-0.5 inline text-amber-600" />
              ≈{fmtHours(yardPhase.typical_hours * chargeFrac)} charging · {' '}
              ≈{fmtHours(yardPhase.typical_hours * (1 - chargeFrac))} parked idle
            </>
          : top
            ? <>mostly {phaseLabel(site.place, top.phase)} — <span className="font-mono">{fmtHours(top.typical_hours)}</span></>
            : 'no phase detail yet'}
      </span>
      <span className="mt-auto pt-1.5 font-mono text-[10px] text-slate-400">
        {site.visits} visits · {fmtHours(site.total_hours)} accumulated
      </span>
    </button>
  )
}

// --------------------------------------------------------------------------- road pair card
function RoadPairCard({ south, north, spreadMax, selected, onSelect, mounted, delay }: {
  south: RoadLeg | null
  north: RoadLeg | null
  spreadMax: number
  selected: Selection | null
  onSelect: (s: Selection | null) => void
  mounted: boolean
  delay: number
}): JSX.Element {
  return (
    <div
      className="flex flex-col justify-center gap-2 transition-opacity duration-300 motion-reduce:transition-none"
      style={{ opacity: mounted ? 1 : 0, transitionDelay: `${delay}ms` }}
    >
      {south && (
        <RoadLegCard leg={south} arrow="→" color={CC.south} textColor="#0369A1"
          spreadMax={spreadMax} selected={selected} onSelect={onSelect} />
      )}
      {north && (
        <RoadLegCard leg={north} arrow="←" color={CC.north} textColor="#6D28D9"
          spreadMax={spreadMax} selected={selected} onSelect={onSelect} />
      )}
    </div>
  )
}

function RoadLegCard({ leg, arrow, color, textColor, spreadMax, selected, onSelect }: {
  leg: RoadLeg
  arrow: string
  color: string
  textColor: string
  spreadMax: number
  selected: Selection | null
  onSelect: (s: Selection | null) => void
}): JSX.Element {
  const isSel = selected?.kind === 'road' && selected.name === leg.stage_name
  const isDim = selected != null && !isSel
  return (
    <button
      type="button"
      onClick={() => onSelect(isSel ? null : { kind: 'road', name: leg.stage_name })}
      className="flex flex-col rounded-lg border px-2.5 py-2 text-left transition-all
                 duration-300 ease-out focus:outline-none focus-visible:ring-2
                 focus-visible:ring-offset-1 motion-reduce:transition-none"
      style={{
        borderColor: isSel ? color : 'rgba(15,23,42,0.07)',
        background: isSel ? `${color}22` : 'rgba(255,255,255,0.6)',
        opacity: isDim ? 0.45 : 1,
        transform: isSel ? 'translateY(-1px)' : undefined,
        boxShadow: isSel ? '0 10px 24px -12px rgba(15,23,42,0.35)' : undefined,
      }}
    >
      <span className="flex items-center justify-between gap-1.5">
        <span className="flex items-center gap-1.5 truncate">
          <span className="font-mono text-[12px] font-bold" style={{ color: textColor }}>{arrow}</span>
          <span className="truncate font-mono text-[13px] font-semibold text-slate-900">
            {fmtHours(leg.typical_hours)}
          </span>
        </span>
        <span className="shrink-0 font-mono text-[8.5px] uppercase tracking-wide" style={{ color: textColor }}>
          {leg.load_state ?? ''}
        </span>
      </span>
      <span className="mt-1.5 block">
        <RangeTriple
          best={leg.best_1_in_10_hours} typical={leg.typical_hours}
          worst={leg.worst_1_in_10_hours} max={spreadMax} dense
        />
      </span>
      <span className="mt-1 font-mono text-[9px] text-slate-400">
        {leg.typical_km ?? '—'} km typical · {leg.observations} runs
      </span>
    </button>
  )
}

// ------------------------------------------------------------------------------ data window
// Bounded to the record's own earliest/latest (data-range), so a selection can't read an
// honestly-empty report as "nothing happened" here. site_visit, round_trip_tat,
// cycle_headline and hub_visit_detail actually narrow to the window; the rest of the page
// (site phases, hub steps, leg mix) stays fleet-wide until those get a bounded variant too.
function DataWindow({ range, value, onChange }: {
  range: DataRange | null
  value: { from: string; to: string } | null
  onChange: (v: { from: string; to: string }) => void
}): JSX.Element | null {
  if (!range || !range.earliest || !range.latest || !value) return null
  const min = range.earliest.slice(0, 16)
  const max = range.latest.slice(0, 16)
  const clamp = (d: string) => (d < min ? min : d > max ? max : d)
  const inputCls =
    'rounded-md border border-slate-200 bg-white px-1.5 py-1 font-mono text-[11px] ' +
    'text-slate-700 focus:border-amber-400 focus:outline-none'
  return (
    <div className="rounded-xl border border-[rgba(15,23,42,0.07)] bg-white/70 px-3.5 py-2.5">
      <div className="flex items-center gap-1.5 font-mono text-[9.5px] font-medium uppercase tracking-[0.14em] text-[#8794A7]">
        <CalendarRange size={11} />
        data window
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <input
          type="datetime-local" value={value.from} min={min} max={max}
          onChange={(e) => {
            const from = clamp(e.target.value || min)
            onChange({ from, to: value.to < from ? from : value.to })
          }}
          className={inputCls}
          aria-label="window start"
        />
        <span className="text-[11px] text-slate-400">→</span>
        <input
          type="datetime-local" value={value.to} min={min} max={max}
          onChange={(e) => {
            const to = clamp(e.target.value || max)
            onChange({ from: value.from > to ? to : value.from, to })
          }}
          className={inputCls}
          aria-label="window end"
        />
      </div>
      <div className="mt-1 text-[9.5px] text-slate-400">
        record runs {min} → {max}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------------------- drill-down
function DrillDown({ site, road, sitePhases, hubSteps, hubVisits, legMix, onClose }: {
  site: SiteVisit | null
  road: RoadLeg | null
  sitePhases: SitePhase[] | null
  hubSteps: HubStep[] | null
  hubVisits: HubVisit[] | null
  legMix: LegSegment[] | null
  onClose: () => void
}): JSX.Element {
  const open = site != null || road != null
  return (
    <div
      className="grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
      style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
    >
      <div className="min-h-0">
        {open && (
          <div className={`${reportCard} ${reportCardPad} animate-slide-up`}>
            {site
              ? <SiteDrillDown site={site} sitePhases={sitePhases} hubSteps={hubSteps} hubVisits={hubVisits} onClose={onClose} />
              : road
                ? <RoadDrillDown road={road} legMix={legMix} onClose={onClose} />
                : null}
          </div>
        )}
      </div>
    </div>
  )
}

function StatChip({ label, value, color }: { label: string; value: number; color: string }): JSX.Element {
  return (
    <span className="inline-flex flex-col rounded-lg border border-[rgba(15,23,42,0.07)] bg-white/70 px-2.5 py-1.5">
      <span className="font-mono text-[8px] font-medium uppercase tracking-[0.12em] text-[#8794A7]">{label}</span>
      <span className="font-mono text-[13px] font-bold leading-tight" style={{ color }}>{fmtHours(value)}</span>
    </span>
  )
}

function DrillHeader({ title, subtitle, best, typical, worst, onClose }: {
  title: string
  subtitle: string
  best: number
  typical: number
  worst: number
  onClose: () => void
}): JSX.Element {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className={reportEyebrow}>{subtitle}</div>
        <h3 className="mt-0.5 text-[15px] font-semibold text-slate-900">{title}</h3>
      </div>
      <div className="flex items-start gap-2">
        <StatChip label="best 1-in-10" value={best} color={CC.ok} />
        <StatChip label="typical" value={typical} color={CC.ink} />
        <StatChip label="worst 1-in-10" value={worst} color={CC.danger} />
        <button
          type="button" onClick={onClose}
          className="mt-1 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  )
}

function ClassPill({ timeClass }: { timeClass: string }): JSX.Element {
  return (
    <span
      className="font-mono text-[9.5px] uppercase tracking-wide"
      style={{ color: classColor(timeClass) }}
    >
      {CLASS_LABEL[timeClass] ?? timeClass}
    </span>
  )
}

function SiteDrillDown({ site, sitePhases, hubSteps, hubVisits, onClose }: {
  site: SiteVisit
  sitePhases: SitePhase[] | null
  hubSteps: HubStep[] | null
  hubVisits: HubVisit[] | null
  onClose: () => void
}): JSX.Element {
  const phases = (sitePhases ?? [])
    .filter((p) => p.place === site.place)
    .sort((a, b) => b.typical_hours - a.typical_hours)
  const maxPhase = phases.reduce((m, p) => Math.max(m, p.typical_hours), 0) || 1
  const chargeFrac = chargingFraction(site.place, hubSteps)

  // hub_time_split is per fence-level hub_code; group the codes belonging to this site so
  // Jhulwania's charging/rest split (and any other instrumented fence) shows up under it.
  const stepGroups = useMemo(() => {
    const groups = new Map<string, HubStep[]>()
    for (const s of hubSteps ?? []) {
      if (placeOf(s.hub_code) !== site.place) continue
      const g = groups.get(s.hub_code)
      if (g) g.push(s)
      else groups.set(s.hub_code, [s])
    }
    for (const g of groups.values()) g.sort((a, b) => a.step - b.step)
    return [...groups.entries()].sort((a, b) => b[1].length - a[1].length)
  }, [hubSteps, site.place])

  // hub_visit_detail is ordered newest-first fleet-wide; filtering to this site's fences
  // keeps that order, so the first 15 are simply the most recent records here.
  const visits = (hubVisits ?? [])
    .filter((v) => placeOf(v.hub_code) === site.place)
    .slice(0, 15)

  return (
    <>
      <DrillHeader
        title={`${SITE_LABEL[site.place] ?? site.place} — one whole visit`}
        subtitle={`${site.visits} visits · ${fmtHours(site.total_hours)} accumulated`}
        best={site.best_1_in_10_hours} typical={site.typical_hours}
        worst={site.worst_1_in_10_hours}
        onClose={onClose}
      />

      <p className="mb-4 max-w-2xl text-[12px] leading-relaxed text-slate-500">
        {SITE_SEQUENCE[site.place] ?? ''}
      </p>

      {phases.length > 0 && (
        <div>
          <div className="mb-2 text-[11px] font-medium text-slate-500">
            Where a visit goes — each phase summed per visit, then the median across visits
          </div>
          <div className="space-y-2">
            {phases.map((p) => {
              const isYard = p.phase === YARD_PHASE[site.place] && chargeFrac != null && chargeFrac > 0 && chargeFrac < 1
              const w = Math.max((100 * p.typical_hours) / maxPhase, 1)
              return (
                <div key={p.phase}>
                  <div className="flex items-center gap-2">
                    <span className="w-40 shrink-0 truncate text-[12px] capitalize text-slate-700">
                      {phaseLabel(site.place, p.phase)}
                    </span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                      {isYard ? (
                        <div className="flex h-full" style={{ width: `${w}%` }}>
                          <div
                            className="h-full"
                            style={{ width: `${100 * (chargeFrac ?? 0)}%`, background: CC.warnBright }}
                            title={`actually charging: ≈${fmtHours(p.typical_hours * (chargeFrac ?? 0))}`}
                          />
                          <div
                            className="h-full"
                            style={{ flex: 1, background: '#94A3B8' }}
                            title={`parked, not charging: ≈${fmtHours(p.typical_hours * (1 - (chargeFrac ?? 0)))}`}
                          />
                        </div>
                      ) : (
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${w}%`, background: classColor(p.time_class) }}
                        />
                      )}
                    </div>
                    <span className="w-16 shrink-0 text-right font-mono text-[11.5px] font-semibold text-slate-800">
                      {fmtHours(p.typical_hours)}
                    </span>
                    <span className="w-24 shrink-0 text-right font-mono text-[10px] text-slate-400">
                      {p.visits}× · {fmtHours(p.total_hours)} total
                    </span>
                    <span className="w-24 shrink-0 text-right">
                      <ClassPill timeClass={p.time_class} />
                    </span>
                  </div>
                  {isYard && (
                    <div className="ml-40 mt-1 flex items-center gap-1 pl-2 text-[10.5px] text-slate-500">
                      <Zap size={10} className="text-amber-600" />
                      only ≈{Math.round(100 * (chargeFrac ?? 0))}% of this dwell draws charge
                      (≈{fmtHours(p.typical_hours * (chargeFrac ?? 0))}) — the rest,
                      ≈{fmtHours(p.typical_hours * (1 - (chargeFrac ?? 0)))}, is parked without
                      charging. Split read from the pack&apos;s charge-current record, which the
                      geofence cannot see.
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {stepGroups.map(([hubCode, steps]) => (
        <div key={hubCode} className="mt-5">
          <div className="mb-2 text-[11px] font-medium text-slate-500">
            Charging, waiting, and what happens after
            {stepGroups.length > 1 || hubCode !== site.place ? (
              <span className="ml-1 font-mono text-[10px] text-slate-400">({hubCode})</span>
            ) : null}
          </div>
          <div className="flex h-9 overflow-hidden rounded-lg ring-1 ring-[rgba(15,23,42,0.07)]">
            {steps.map((s) => (
              <div
                key={s.step}
                title={`${s.activity}: ${fmtHours(s.typical_hours)} typical, ${s.share_of_hours_pct.toFixed(0)}% of hours here`}
                style={{
                  width: `${Math.max(s.share_of_hours_pct, 2)}%`,
                  background: s.charging ? CC.warnBright : 'rgba(15,23,42,0.08)',
                }}
                className="flex items-center justify-center gap-1 border-r border-white/60 last:border-r-0"
              >
                {s.charging && <Zap size={12} className="text-white" />}
                {s.share_of_hours_pct >= 14 && (
                  <span className={`font-mono text-[9px] font-semibold ${s.charging ? 'text-white' : 'text-slate-500'}`}>
                    {s.share_of_hours_pct.toFixed(0)}%
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {steps.map((s) => (
              <div key={s.step} className="rounded-lg border border-[rgba(15,23,42,0.07)] bg-white/70 px-2.5 py-2">
                <div className="flex items-center gap-1 text-[10.5px] font-medium text-slate-500">
                  {s.charging && <Zap size={10} className="text-amber-600" />}
                  {s.activity}
                </div>
                <div className="mt-0.5 font-mono text-[13px] font-semibold text-slate-900">{fmtHours(s.typical_hours)}</div>
                <div className="text-[10.5px] text-slate-400">worst 1 in 10: {fmtHours(s.worst_1_in_10_hours)}</div>
                <div className={`mt-1 text-[10px] ${s.charging || s.can_we_cut_it.startsWith('No') ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {s.can_we_cut_it}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {visits.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] font-medium text-slate-500">Recent visits, the chain of events</div>
            <span className="text-[10.5px] text-slate-400">
              {visits.length} most recent · per-fence records — one site visit can span several rows
            </span>
          </div>
          <div className="overflow-hidden rounded-xl ring-1 ring-[rgba(15,23,42,0.07)]">
            <table className="w-full text-[11.5px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                  <th className="px-2.5 py-1.5 font-medium">Vehicle</th>
                  <th className="px-2.5 py-1.5 font-medium">Fence</th>
                  <th className="px-2.5 py-1.5 font-medium">Arrived</th>
                  <th className="px-2.5 py-1.5 font-medium text-right">Dwell</th>
                  <th className="px-2.5 py-1.5 font-medium text-right">Charging</th>
                  <th className="px-2.5 py-1.5 font-medium text-right">SoC</th>
                  <th className="px-2.5 py-1.5 font-medium text-right">TAT</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v, vi) => (
                  <tr
                    key={v.visit_id}
                    className="animate-row-in border-b border-slate-50 transition-colors duration-100 last:border-b-0 even:bg-slate-50/40 hover:bg-amber-50/40"
                    style={{ animationDelay: `${Math.min(vi, 12) * 16}ms` }}
                  >
                    <td className="px-2.5 py-1.5 font-mono text-slate-700">{v.vehicle_id}</td>
                    <td className="px-2.5 py-1.5 font-mono text-[10.5px] text-slate-500">{v.hub_code}</td>
                    <td className="px-2.5 py-1.5 text-slate-500">
                      {Number.isFinite(v.arrival_ts)
                        ? new Date(v.arrival_ts).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td className="px-2.5 py-1.5 text-right font-mono text-slate-700">{fmtHours(v.dwell_hours)}</td>
                    <td className="px-2.5 py-1.5 text-right">
                      {v.charging_hours > 0 && v.dwell_hours > 0 ? (
                        <span className="inline-flex items-center justify-end gap-1.5">
                          <span className="h-1.5 w-10 overflow-hidden rounded-full bg-slate-100">
                            <span
                              className="block h-full rounded-full"
                              style={{
                                width: `${Math.min(100, (100 * v.charging_hours) / v.dwell_hours)}%`,
                                background: CC.warnBright,
                              }}
                              title={`${fmtHours(v.charging_hours)} of ${fmtHours(v.dwell_hours)} dwell on charge`}
                            />
                          </span>
                          <span className="font-mono text-slate-700">{fmtHours(v.charging_hours)}</span>
                        </span>
                      ) : (
                        <span className="font-mono text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-right text-slate-500">
                      {v.soc_arrival != null && v.soc_departure != null
                        ? `${v.soc_arrival}% → ${v.soc_departure}%`
                        : '—'}
                    </td>
                    <td className="px-2.5 py-1.5 text-right">
                      {v.tat_breached == null ? (
                        <span className="text-slate-300">—</span>
                      ) : v.tat_breached ? (
                        <span className="font-medium text-rose-700">breached</span>
                      ) : (
                        <span className="text-emerald-700">on time</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {phases.length === 0 && stepGroups.length === 0 && visits.length === 0 && (
        <p className="text-[12.5px] text-slate-400">No geotag detail collected here yet.</p>
      )}
    </>
  )
}

function RoadDrillDown({ road, legMix, onClose }: {
  road: RoadLeg
  legMix: LegSegment[] | null
  onClose: () => void
}): JSX.Element {
  const rows = (legMix ?? [])
    .filter((s) => s.stage_name === road.stage_name)
    .sort((a, b) => b.total_hours - a.total_hours)
  const total = rows.reduce((s, r) => s + r.total_hours, 0) || 1

  return (
    <>
      <DrillHeader
        title={road.stage_name}
        subtitle={`${road.load_state ?? ''} · ${road.typical_km ?? '—'} km typical · ${road.observations} runs`}
        best={road.best_1_in_10_hours} typical={road.typical_hours}
        worst={road.worst_1_in_10_hours}
        onClose={onClose}
      />
      {rows.length === 0 ? (
        <p className="text-[12.5px] text-slate-400">No toll, ghat or rest crossings attributed to this leg yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.segment} className="flex items-center gap-2">
              <span className="w-28 shrink-0 truncate text-[12px] capitalize text-slate-700">{r.segment}</span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(r.total_hours / total) * 100}%`,
                    background: r.time_class === 'controllable' ? CC.dangerBright : CC.infoBright,
                  }}
                />
              </div>
              <span className="w-20 shrink-0 text-right font-mono text-[11px] text-slate-500">{r.crossings}×</span>
              <span className="w-16 shrink-0 text-right font-mono text-[11.5px] text-slate-600">{fmtHours(r.total_hours)}</span>
              <span className="w-24 shrink-0 text-right">
                <ClassPill timeClass={r.time_class} />
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
